import dayjs, { Dayjs } from "dayjs";
import { debounce } from "lodash";
import { useSnackbar } from "notistack";

import { useEffect, useRef, useState } from "react";

import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { LoadingButton } from "@mui/lab";
import { Grid } from "@mui/material";

import { HotelAutoCompleteOptionItem, requestHotelAutoCompleteOptions } from "@/api/hotel";
import BaseAutocomplete from "@/components/BaseAutocomplete";
import SearchRangeDate from "@/components/SearchBar/SearchRangeDate";
import { getHotelIconName } from "@/constants/hotel";
import useEvent from "@/hooks/useEvent";
import useHotelSearchCondition from "@/hooks/useHotelSearchCondition";
import usePlatform from "@/hooks/usePlatform";

interface Props {
  searchLoading: boolean;
  onSearch: (params: {
    items: Pick<HotelAutoCompleteOptionItem, "latitude" | "longitude" | "label">[];
    startDate: string;
    endDate: string;
  }) => void;
  onSearchAll: () => void;
  useWhere?: "home" | "hotel";
}

const SearchBar = (props: Props) => {
  const { onSearch, onSearchAll, searchLoading, useWhere = "hotel" } = props;
  const { isMobile, isPc } = usePlatform();
  const [loading, setLoading] = useState(false);

  const {
    condition: searchData,
    changeCondition,
    searchType,
    changeSearchType,
  } = useHotelSearchCondition(useWhere);
  const { enqueueSnackbar } = useSnackbar();

  const [options, setOptions] = useState<HotelAutoCompleteOptionItem[]>(searchData.items);
  const hotelInfo = useRef<HotelAutoCompleteOptionItem[]>(
    searchData.items.map((item) => ({
      ...item,
      latitude: item.latitude,
      longitude: item.longitude,
      label: item.code,
    }))
  );

  const handleChange = useEvent((e) => {
    changeCondition({
      ...searchData,
      items: e,
    });

    const target = options.filter((item) => {
      const names = e.map(({ name }) => name);
      return names.includes(item.value);
    });
    if (target) {
      hotelInfo.current = target;
    }
  });

  const getOptionsData = useEvent(async (keywords: string) => {
    try {
      setOptions([]);

      if (!keywords) {
        return;
      }
      setLoading(true);
      const res = await requestHotelAutoCompleteOptions({ query: keywords });
      if (res.data) {
        setOptions(res.data);
      }
    } finally {
      setLoading(false);
    }
  });

  const searchOptionsData = debounce(getOptionsData, 1000);

  const onDateChange = useEvent((date: [Dayjs | null, Dayjs | null]) => {
    changeCondition({
      ...searchData,
      startDate: date[0],
      endDate: date[1],
    });
  });

  const handleSearch = useEvent(() => {
    if (onSearch && hotelInfo.current.length && searchData.startDate && searchData.endDate) {
      if (dayjs(searchData.startDate).isSame(searchData.endDate, "D")) {
        enqueueSnackbar(`Please select a different day!`, {
          variant: "info",
          anchorOrigin: {
            vertical: isMobile ? "bottom" : "top",
            horizontal: isMobile ? "center" : "right",
          },
          autoHideDuration: 2000,
        });
        return;
      }

      changeSearchType("booking");
      onSearch({
        items: hotelInfo.current.map((idx) => ({
          latitude: idx.latitude,
          longitude: idx.longitude,
          label: idx.label,
          type: idx.type,
        })),
        startDate: dayjs(searchData.startDate).format("YYYY-MM-DD"),
        endDate: dayjs(searchData.endDate).format("YYYY-MM-DD"),
      });
    }
  });

  useEffect(() => {
    if (useWhere === "home") {
      return;
    }
    // 带搜索条件自动触发搜索
    if (searchData.endDate && searchData.startDate && searchData.items.length > 0) {
      handleSearch();
    } else {
      onSearchAll();
      changeSearchType("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative w-full">
      <Grid
        container
        rowSpacing={2}
        columnSpacing={1}
        className="relative"
        sx={{ paddingRight: isPc && useWhere === "hotel" ? "64px" : 0 }}
      >
        <Grid item xs={12} sm={6}>
          <BaseAutocomplete
            placeholder="e.g city, landmark, address"
            Icon={<MapOutlinedIcon />}
            options={options.map((item: HotelAutoCompleteOptionItem) => {
              return {
                code: item.label,
                name: item.value,
                type: item.type,
                ...item,
              };
            })}
            selectData={searchData.items}
            onChange={handleChange}
            loading={loading}
            maxCount={1000000000}
            limitTags={3}
            freeSolo={false}
            rowKey={(option) => `${option.code}-${option.name}`}
            onInputChange={searchOptionsData}
            renderItem={(option) => {
              const IconName = getHotelIconName(option.type);

              return (
                <div className="pl-[4px]">
                  <div className="flex items-center space-x-[10px]">
                    <IconName />
                    <div className="flex flex-col flex-1">
                      <span className="text-sm">{option.code}</span>
                      <span className="text-ellipsis overflow-hidden whitespace-nowrap text-xs">
                        {option.name}
                      </span>
                    </div>
                  </div>
                </div>
              );
            }}
            ModalProps={{ style: { zIndex: 20000 } }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SearchRangeDate
            label="Check-in date - Check-out date"
            defaultValue={[
              searchData.startDate ? dayjs(searchData.startDate) : null,
              searchData.endDate ? dayjs(searchData.endDate) : null,
            ]}
            onChange={onDateChange}
            maxRangeDays={7}
            calendarRangeDays={365}
            needSameDate={false}
          />
        </Grid>

        {isPc && useWhere === "hotel" && (
          <div className="flex items-center justify-end mt-[15px] ml-[10px] absolute right-0">
            <LoadingButton
              color="primary"
              loading={searchLoading}
              variant="contained"
              className="!min-w-[48px] !h-[56px] !w-[56px]"
              onClick={handleSearch}
            >
              <SearchIcon sx={{ fontSize: "24px" }} />
            </LoadingButton>
          </div>
        )}
      </Grid>

      {isMobile && useWhere === "hotel" && (
        <div className="pb-[18px] pt-[10px]">
          <LoadingButton
            variant="contained"
            className="w-full"
            loading={searchLoading}
            startIcon={<SearchIcon />}
            onClick={handleSearch}
          >
            Search
          </LoadingButton>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
